import type { StaticEntityType } from "@config";
import { cn } from "@lib/cn";
import "../styles/index.scss";
import { environment } from "@environment";
import { validateDynamicVariable } from "@/shared";
import type {
  AttachmentProps,
  EditorDataProps,
  EditorSlotsProps,
} from "../types";
import { handleAttachment } from "../utils";

const attachmentAssets = `${environment.assetsLink}/assets/images/Attachment/`;

export const AttachmentLibrary = ({
  entity,
  editorData,
  editorSlots,
}: {
  entity: StaticEntityType;
  editorData?: EditorDataProps;
  editorSlots?: EditorSlotsProps;
}) => {
  const { settings } = entity as StaticEntityType<AttachmentProps>;

  const width = editorData?.width || settings.width;
  const height = editorData?.height || settings.height;

  const { hasDynamicVariable } = validateDynamicVariable(
    settings.dynamicVariableLinkUrl,
  );

  const { isEmpty, url, previewType, fileIcon } = handleAttachment({
    settings,
  });

  const handleClick = async () => {
    if (!!editorData || isEmpty) return;
    const newUrl = new URL(url);
    if (settings.previewType === "pdf") newUrl.hash = "";

    if (settings.attachmentType === "link") window.open(newUrl, "_blank");
    else if (settings.attachmentType === "file") {
      try {
        const data = await fetch(newUrl);
        const blob = await data.blob();
        const objectUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.setAttribute("href", objectUrl);
        link.setAttribute("download", settings.fileName!);

        link.click();
        link.remove();
      } catch (e) {
        console.log(e);
      }
    }
  };

  return (
    <div
      ref={editorData?.elementRef}
      className="attachment-pzgD9OfeO5cL1yok"
      onClick={handleClick}
      data-shape={settings.shape}
      data-has-dynamic-var={hasDynamicVariable}
      style={{
        justifyContent: settings.horizontalAlignment,
      }}
    >
      {editorSlots?.resizeSlot}
      <div
        className="attachment-pzgD9OfeO5cL1yok__layout"
        data-is-empty={isEmpty}
        data-icon-source={fileIcon}
        {...EDITOR_ATTRIBUTES({ isEditor: !!editorData })}
        style={{
          borderRadius: settings.round,
          width: width + "%",
          height: settings.shape === "axis-resize" ? height : "auto",
        }}
      >
        {editorSlots?.dynamicVariablePlateSlot}
        {editorSlots?.dynamicVariableDotSlot}
        {!isEmpty && (
          <button
            className="attachment-pzgD9OfeO5cL1yok-link-btn"
            type="button"
          >
            <img
              src={
                attachmentAssets +
                (settings.attachmentType === "link"
                  ? "link.svg"
                  : "download.svg")
              }
              width="20"
              height="20"
              alt=""
            />
          </button>
        )}
        <div className="attachment-pzgD9OfeO5cL1yok__inner-wrapper">
          <div
            className="attachment-pzgD9OfeO5cL1yok__content"
            data-preview-type={
              previewType === "pdf" &&
              settings.preferredPreviewType === "document"
                ? "document"
                : "icon"
            }
          >
            {!isEmpty ? (
              previewType === "pdf" &&
              settings.preferredPreviewType === "document" ? (
                <iframe
                  src={url}
                  title="Attachment iframe"
                  className={cn(
                    "tw-border-none",
                    editorData ? "tw-pointer-events-none" : "",
                  )}
                />
              ) : (
                <div className="attachment-pzgD9OfeO5cL1yok__file-icon">
                  <div>
                    <img
                      src={`${attachmentAssets}${fileIcon}.svg`}
                      alt="File icon"
                      width="auto"
                    />
                    {fileIcon !== "unknown-file" && (
                      <img
                        src={`${attachmentAssets}attachment-in-circle.svg`}
                        className="attachment-pzgD9OfeO5cL1yok__file-icon-attachment"
                        alt="attachment icon"
                      />
                    )}
                  </div>
                </div>
              )
            ) : (
              <>
                <div className="attachment-pzgD9OfeO5cL1yok-empty-icon">
                  <img
                    alt="Attachment icon"
                    src={attachmentAssets + "attachment.svg"}
                    width="40"
                    height="40"
                  />
                </div>
                <button
                  className="attachment-pzgD9OfeO5cL1yok-add-btn"
                  type="button"
                  onClick={(e) => {
                    if (!editorData) return;
                    e.preventDefault();
                    e.stopPropagation();
                    editorData?.selectActiveControl();
                  }}
                >
                  <img
                    alt="Plus icon"
                    src={attachmentAssets + "plus.svg"}
                    width="16"
                    height="16"
                  />
                  Add attachment
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const EDITOR_ATTRIBUTES = ({ isEditor }: { isEditor: boolean }) => {
  const payload: Record<string, string> = {
    "data-dnd-trigger": "true",
  };
  return isEditor ? payload : {};
};
